import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { donationStats } from '@/mocks/community';

const presetAmounts = [10, 30, 50, 100, 200, 500];

const impactItems = [
  { amount: 10, icon: 'ri-cup-line', label: '奶粉一罐', desc: '为一名婴儿提供一周的营养奶粉' },
  { amount: 30, icon: 'ri-shirt-line', label: '保暖衣物', desc: '为一名婴儿提供一套冬季保暖衣物' },
  { amount: 50, icon: 'ri-medicine-bottle-line', label: '基础医疗', desc: '为一名婴儿提供一次基础健康检查' },
  { amount: 100, icon: 'ri-home-heart-line', label: '一个月照料', desc: '资助一名婴儿一个月的基本生活所需' },
];

const stories = [
  {
    id: 1,
    name: '小安',
    age: '8个月',
    country: '埃塞俄比亚',
    image: 'https://public.readdy.ai/ai/img_res/059fbe4f86229eda09dce27777335842.jpg',
    story: '小安出生在埃塞俄比亚的一个偏远村庄，妈妈因病无法哺乳，家里买不起奶粉。在爱心人士的帮助下，小安现在每天都能喝到营养奶粉，脸上有了笑容。',
    helped: true,
  },
  {
    id: 2,
    name: '玛丽亚',
    age: '5个月',
    country: '孟加拉国',
    image: 'https://readdy.ai/api/search-image?query=cute%20Asian%20baby%20infant%20girl%20with%20chubby%20cheeks%2C%20innocent%20eyes%2C%20soft%20warm%20lighting%2C%20simple%20clean%20background%2C%20touching%20portrait%20photography&width=400&height=400&seq=baby002&orientation=squarish',
    story: '玛丽亚的父母都是农民，收入极低。冬天来临时，家里没有足够的衣物为她保暖。感谢捐助者，她现在有了温暖的衣物，健康成长。',
    helped: true,
  },
  {
    id: 3,
    name: '托比',
    age: '3个月',
    country: '尼日利亚',
    image: 'https://readdy.ai/api/search-image?query=sweet%20African%20baby%20infant%20boy%20with%20gentle%20expression%2C%20soft%20natural%20light%2C%20warm%20tones%2C%20simple%20background%2C%20emotional%20portrait%20photography&width=400&height=400&seq=baby003&orientation=squarish',
    story: '托比出生时体重不足，需要特殊的营养支持。他的家庭无力承担医疗费用。您的捐款可以帮助像托比这样的婴儿获得必要的医疗照护。',
    helped: false,
  },
];

const donors = [
  { name: '王**', amount: 500, time: '5分钟前', avatar: '王' },
  { name: '李**', amount: 100, time: '12分钟前', avatar: '李' },
  { name: '张**', amount: 200, time: '28分钟前', avatar: '张' },
  { name: '爱心人士', amount: 50, time: '1小时前', avatar: '❤' },
  { name: '陈**', amount: 1000, time: '2小时前', avatar: '陈' },
];

// Deterministic QR pattern
function QRPattern({ seed, size = 120 }: { seed: number; size?: number }) {
  const cells = 21;
  const pattern = Array.from({ length: cells * cells }, (_, i) => {
    const row = Math.floor(i / cells);
    const col = i % cells;
    const inFinder = (
      (row < 7 && col < 7) ||
      (row < 7 && col >= cells - 7) ||
      (row >= cells - 7 && col < 7)
    );
    if (inFinder) {
      const localR = row < 7 ? row : row - (cells - 7);
      const localC = col < 7 ? col : col - (cells - 7);
      return (localR === 0 || localR === 6 || localC === 0 || localC === 6 || (localR >= 2 && localR <= 4 && localC >= 2 && localC <= 4));
    }
    return ((i * 7 + seed * 13 + row * 3 + col * 5) % 3 !== 0);
  });
  const cellSize = size / cells;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      {pattern.map((filled, i) => {
        if (!filled) return null;
        const row = Math.floor(i / cells);
        const col = i % cells;
        return <rect key={i} x={col * cellSize} y={row * cellSize} width={cellSize} height={cellSize} fill="#1a1a1a" />;
      })}
    </svg>
  );
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const donateSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/donate`,
  url: `${SITE_URL}/donate`,
  name: '爱心捐助 - 帮助农村学生 | 金榜旺财',
  description: '金榜旺财爱心捐助平台，富裕家长小额捐款，平台统一购买文具、书籍、衣物，直接邮寄给农村学生，每笔捐款公开透明，让每一个孩子都被温柔以待。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '爱心捐助', item: `${SITE_URL}/donate` },
    ],
  },
};

export default function DonatePage() {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(50);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [payMethod, setPayMethod] = useState<'wechat' | 'alipay' | 'card'>('wechat');
  const [qrCountdown, setQrCountdown] = useState(300);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (payMethod === 'wechat' || payMethod === 'alipay') {
      setQrCountdown(300);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setQrCountdown(prev => {
          if (prev <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [payMethod]);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const id = 'schema-donate';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(donateSchema);
    document.title = '爱心捐助 - 帮助农村学生 | 金榜旺财';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '金榜旺财爱心捐助平台，富裕家长小额捐款，平台统一购买文具、书籍、衣物，直接邮寄给农村学生，每笔捐款公开透明。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  const finalAmount = customAmount ? Number(customAmount) : selectedAmount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!finalAmount || finalAmount <= 0) { setFormError('请选择或输入捐款金额'); return; }
    if (!donorEmail && !donorPhone) { setFormError('请填写邮箱或手机号以便联系'); return; }
    setSubmitting(true);
    const body = new URLSearchParams();
    body.append('amount', String(finalAmount));
    body.append('name', anonymous ? '匿名爱心人士' : donorName || '爱心人士');
    body.append('email', donorEmail);
    body.append('phone', donorPhone);
    body.append('message', message);
    body.append('anonymous', String(anonymous));
    try {
      await fetch('https://readdy.ai/api/form/d7cq9num3fsfopf23i2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setFormError('提交失败，请稍后重试');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 overflow-hidden">
        <div className="relative h-[420px] md:h-[520px]">
          <img
            src="https://readdy.ai/api/search-image?query=group%20of%20diverse%20adorable%20babies%20and%20infants%20from%20different%20countries%2C%20warm%20golden%20light%2C%20hopeful%20and%20innocent%20expressions%2C%20soft%20focus%20background%2C%20heartwarming%20and%20emotional%20photography%2C%20beautiful%20composition&width=1440&height=520&seq=donate_hero&orientation=landscape"
            alt="帮助婴儿"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <span className="inline-block px-4 py-1.5 bg-rose-500/80 text-white text-xs font-bold rounded-full mb-5 tracking-widest uppercase">爱心公益 · 全球婴儿救助</span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4 leading-tight">
              让每一个小生命<br />都被温柔以待
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed mb-8">
              世界上还有许多婴儿缺少奶粉、衣物和基本医疗。您的一份爱心，可以点亮一个小生命的希望。
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="#donate-form" className="px-8 py-3.5 bg-rose-500 text-white rounded-full font-bold text-base shadow-xl hover:bg-rose-400 hover:-translate-y-0.5 transition-all cursor-pointer">
                <i className="ri-heart-fill mr-2"></i>立即捐款
              </a>
              <a href="#stories" className="px-8 py-3.5 bg-white/20 text-white border border-white/40 rounded-full font-bold text-base hover:bg-white/30 transition-all cursor-pointer">
                了解他们的故事
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-rose-600 text-white py-6">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { num: donationStats.totalDonors.toLocaleString(), label: '爱心捐助者', icon: 'ri-user-heart-line' },
            { num: `¥${(donationStats.totalAmount / 10000).toFixed(0)}万+`, label: '累计捐款金额', icon: 'ri-money-cny-circle-line' },
            { num: donationStats.infantsHelped.toLocaleString(), label: '受助婴儿', icon: 'ri-bear-smile-line' },
            { num: `${donationStats.countries}个`, label: '覆盖国家地区', icon: 'ri-global-line' },
          ].map((s) => (
            <div key={s.label}>
              <div className="flex items-center justify-center mb-1">
                <i className={`${s.icon} text-white/60 text-lg mr-1`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-black">{s.num}</div>
              <div className="text-white/70 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16">

        {/* Impact Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">您的捐款能做什么</h2>
          <p className="text-gray-500 text-sm">每一分钱都会直接用于帮助有需要的婴儿</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-20">
          {impactItems.map((item) => (
            <div key={item.amount} className="bg-white rounded-2xl p-6 text-center border border-gray-100 hover:border-rose-200 hover:-translate-y-1 transition-all cursor-pointer group">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-50 rounded-2xl mx-auto mb-4 group-hover:bg-rose-100 transition-colors">
                <i className={`${item.icon} text-rose-500 text-2xl`}></i>
              </div>
              <div className="text-2xl font-black text-rose-600 mb-1">¥{item.amount}</div>
              <div className="font-bold text-gray-900 text-sm mb-2">{item.label}</div>
              <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Baby Stories */}
        <div id="stories" className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">他们需要您的帮助</h2>
            <p className="text-gray-500 text-sm">每一个小生命都值得被爱</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stories.map((story) => (
              <div key={story.id} className="bg-white rounded-3xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all">
                <div className="relative h-56">
                  <img src={story.image} alt={story.name} className="w-full h-full object-cover object-top" />
                  {story.helped && (
                    <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-check-line mr-1"></i>已获帮助
                    </div>
                  )}
                  {!story.helped && (
                    <div className="absolute top-4 right-4 bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                      急需帮助
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-black text-gray-900 text-lg">{story.name}</h3>
                    <span className="text-xs text-gray-400">{story.age}</span>
                    <span className="ml-auto text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded-full">{story.country}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{story.story}</p>
                  {!story.helped && (
                    <a href="#donate-form" className="block w-full py-2.5 bg-rose-500 text-white text-center rounded-full text-sm font-bold hover:bg-rose-600 transition-colors cursor-pointer">
                      帮助{story.name} <i className="ri-heart-fill ml-1"></i>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Donation Form + Recent Donors */}
        <div id="donate-form" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100">
                <div className="w-24 h-24 flex items-center justify-center bg-rose-50 rounded-full mx-auto mb-6">
                  <i className="ri-heart-fill text-5xl text-rose-500"></i>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-3">感谢您的爱心！</h2>
                <p className="text-gray-500 mb-2">您的捐款 <span className="font-bold text-rose-600">¥{finalAmount}</span> 已成功提交</p>
                <p className="text-gray-400 text-sm mb-8">我们会将您的爱心传递给最需要帮助的婴儿，感谢您让这个世界更温暖。</p>
                <button onClick={() => setSubmitted(false)} className="px-8 py-3 bg-rose-500 text-white rounded-full font-bold hover:bg-rose-600 transition-colors cursor-pointer">
                  再次捐款
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border border-gray-100">
                <h2 className="text-2xl font-black text-gray-900 mb-2">选择捐款金额</h2>
                <p className="text-gray-500 text-sm mb-6">每一分爱心都会直接帮助到有需要的婴儿</p>

                {/* Amount Selector */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {presetAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                      className={`py-3 rounded-2xl text-sm font-bold transition-all cursor-pointer border-2 ${
                        selectedAmount === amt && !customAmount
                          ? 'border-rose-500 bg-rose-50 text-rose-600'
                          : 'border-gray-200 text-gray-700 hover:border-rose-300'
                      }`}
                    >
                      ¥{amt}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex-1 flex items-center bg-gray-50 border-2 border-gray-200 rounded-2xl px-4 py-3 focus-within:border-rose-400 transition-colors">
                    <span className="text-gray-500 font-bold mr-2">¥</span>
                    <input
                      type="number"
                      min="1"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                      placeholder="自定义金额"
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                  </div>
                  {finalAmount && (
                    <div className="text-sm text-gray-500 whitespace-nowrap">
                      = 约 <span className="font-bold text-rose-600">{Math.floor((finalAmount || 0) / 10)}</span> 罐奶粉
                    </div>
                  )}
                </div>

                {/* Donor Info */}
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">捐款人信息</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} className="w-4 h-4 accent-rose-500" />
                        <span className="text-sm text-gray-600">匿名捐款</span>
                      </label>
                    </div>
                    {!anonymous && (
                      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-rose-400 transition-colors">
                        <i className="ri-user-line text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          name="name"
                          value={donorName}
                          onChange={e => setDonorName(e.target.value)}
                          placeholder="您的姓名（选填）"
                          className="flex-1 bg-transparent text-sm outline-none"
                        />
                      </div>
                    )}
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-rose-400 transition-colors">
                      <i className="ri-mail-line text-gray-400 text-sm"></i>
                      <input
                        type="email"
                        name="email"
                        value={donorEmail}
                        onChange={e => setDonorEmail(e.target.value)}
                        placeholder="邮箱（用于接收捐款凭证）"
                        className="flex-1 bg-transparent text-sm outline-none"
                      />
                    </div>
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-rose-400 transition-colors">
                      <i className="ri-phone-line text-gray-400 text-sm"></i>
                      <input
                        type="tel"
                        name="phone"
                        value={donorPhone}
                        onChange={e => setDonorPhone(e.target.value)}
                        placeholder="手机号（选填）"
                        className="flex-1 bg-transparent text-sm outline-none"
                      />
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-rose-400 transition-colors">
                      <textarea
                        name="message"
                        value={message}
                        onChange={e => { if (e.target.value.length <= 500) setMessage(e.target.value); }}
                        placeholder="留下您的爱心寄语（选填，最多500字）"
                        className="w-full bg-transparent text-sm outline-none resize-none h-20"
                        maxLength={500}
                      ></textarea>
                      <div className="text-right text-xs text-gray-400">{message.length}/500</div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="font-bold text-gray-900 mb-3">选择支付方式</h3>
                  <div className="flex gap-2 mb-4">
                    {[
                      { key: 'wechat', label: '微信支付', icon: 'ri-wechat-pay-fill', color: 'text-green-600', activeBorder: 'border-green-500', activeBg: 'bg-green-50' },
                      { key: 'alipay', label: '支付宝', icon: 'ri-alipay-fill', color: 'text-sky-500', activeBorder: 'border-sky-400', activeBg: 'bg-sky-50' },
                      { key: 'card', label: '银行卡', icon: 'ri-bank-card-fill', color: 'text-orange-500', activeBorder: 'border-orange-400', activeBg: 'bg-orange-50' },
                    ].map(m => (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setPayMethod(m.key as typeof payMethod)}
                        className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-all cursor-pointer ${payMethod === m.key ? `${m.activeBorder} ${m.activeBg}` : 'border-gray-200 bg-white'}`}
                      >
                        <i className={`${m.icon} text-xl ${payMethod === m.key ? m.color : 'text-gray-300'}`}></i>
                        <span className={`text-xs font-semibold ${payMethod === m.key ? 'text-gray-700' : 'text-gray-400'}`}>{m.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* WeChat / Alipay QR */}
                  {(payMethod === 'wechat' || payMethod === 'alipay') && finalAmount && (
                    <div className={`rounded-2xl p-4 border mb-4 ${payMethod === 'wechat' ? 'bg-green-50 border-green-100' : 'bg-sky-50 border-sky-100'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <i className={`${payMethod === 'wechat' ? 'ri-wechat-pay-fill text-green-600' : 'ri-alipay-fill text-sky-500'} text-base`}></i>
                          <span className="text-sm font-semibold text-gray-700">{payMethod === 'wechat' ? '微信扫码捐款' : '支付宝扫码捐款'}</span>
                        </div>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {qrCountdown > 0 ? formatCountdown(qrCountdown) : '已过期'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`relative p-2 bg-white border-2 rounded-xl flex-shrink-0 ${qrCountdown === 0 ? 'opacity-40 border-gray-200' : payMethod === 'wechat' ? 'border-green-200' : 'border-sky-200'}`}>
                          <QRPattern seed={(finalAmount || 50) + (payMethod === 'wechat' ? 77 : 99)} size={90} />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-md ${payMethod === 'wechat' ? 'bg-green-500' : 'bg-sky-500'}`}>
                              <i className={`${payMethod === 'wechat' ? 'ri-wechat-pay-fill' : 'ri-alipay-fill'} text-white text-xs`}></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800 mb-1">
                            打开{payMethod === 'wechat' ? '微信' : '支付宝'}扫码
                          </p>
                          <p className="text-xs text-gray-500 mb-2">捐款金额：<strong className="text-rose-600">¥{finalAmount}</strong></p>
                          <div className="bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            <p className="text-xs text-amber-700">演示模式，正式上线后显示真实二维码</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {formError && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-4">{formError}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-4 text-white rounded-2xl font-black text-lg shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer disabled:opacity-60 ${
                    payMethod === 'wechat' ? 'bg-green-600 hover:bg-green-500' :
                    payMethod === 'alipay' ? 'bg-sky-500 hover:bg-sky-400' :
                    'bg-rose-500 hover:bg-rose-600'
                  }`}
                >
                  {submitting ? '提交中...' : payMethod === 'wechat' ? `微信支付 ¥${finalAmount || '?'}` : payMethod === 'alipay' ? `支付宝支付 ¥${finalAmount || '?'}` : `确认捐款 ¥${finalAmount || '?'}`}
                  <i className="ri-heart-fill ml-2"></i>
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  <i className="ri-shield-check-line mr-1"></i>
                  您的信息安全加密，捐款100%用于婴儿救助
                </p>
              </form>
            )}
          </div>

          {/* Sidebar: Recent Donors + Share */}
          <div className="flex flex-col gap-6">
            {/* Recent Donors */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-heart-pulse-line text-rose-500"></i>
                最新爱心捐款
              </h3>
              <div className="flex flex-col gap-4">
                {donors.map((d, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-sm flex-shrink-0">
                      {d.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{d.name}</p>
                      <p className="text-xs text-gray-400">{d.time}</p>
                    </div>
                    <span className="text-rose-600 font-black text-sm whitespace-nowrap">¥{d.amount}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Share Card */}
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-3xl p-6 text-white">
              <h3 className="font-bold text-lg mb-2">传播爱心</h3>
              <p className="text-white/80 text-sm mb-5 leading-relaxed">
                分享给更多朋友，让更多人知道这些需要帮助的小生命。
              </p>
              <div className="flex flex-col gap-2">
                <button className="w-full py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer">
                  <i className="ri-wechat-line mr-2"></i>分享到微信
                </button>
                <button className="w-full py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer">
                  <i className="ri-weibo-line mr-2"></i>分享到微博
                </button>
                <button className="w-full py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer">
                  <i className="ri-link-m mr-2"></i>复制链接
                </button>
              </div>
            </div>

            {/* Transparency */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-shield-check-line text-green-500"></i>
                公益透明承诺
              </h3>
              <div className="flex flex-col gap-3 text-sm text-gray-600">
                {[
                  '100% 捐款直接用于婴儿救助',
                  '每月公开捐款使用明细报告',
                  '与国际认证公益机构合作',
                  '每笔捐款均有电子凭证',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
